/**
 * Single source of truth for the curated Sociale Kaart specialisation
 * vocabulary. Three consumers must agree on this list:
 *
 *   - backend/scripts/seed-providers.ts    (normalizes Excel rows to these)
 *   - frontend/src/pages/SocialeKaart.tsx  (practitioner filter chip rail)
 *   - frontend/src/pages/BeheerSocialeKaart.tsx (admin form chip picker)
 *
 * Adding a chip here without updating the seed normalizer's synonym map
 * means the chip will exist but match zero providers (no Excel row maps
 * to it). Removing a chip means existing seeded data on Firestore retains
 * the old tag — re-seed to clean up.
 *
 * Keep alphabetical so the chip rail renders predictably.
 */
export const CANONICAL_SPECIALISATIONS = [
    'ACT',
    'ADD',
    'ADHD',
    'Angst',
    'Autisme',
    'Bipolaire stoornis',
    'Burn-out',
    'CGT',
    'Depressie',
    'Dissociatie',
    'Dwang',
    'Dyscalculie',
    'Dyslexie',
    'Eetstoornissen',
    'EMDR',
    'Emotieregulatie',
    'Faalangst',
    'Fobie',
    'Forensische psychiatrie',
    'Gedragsproblemen',
    'Gender',
    'Gezin en systeem',
    'Grensoverschrijdend gedrag',
    'Hechtingsproblematiek',
    'Hoogbegaafdheid',
    'Hoogsensitiviteit',
    'Leerproblemen',
    'LVB',
    'Mediation',
    'Meditatie',
    'Mindfulness',
    'Naasten',
    'NAH',
    'Neurodiversiteit',
    'OCS',
    'Ouderen',
    'Overgang',
    'Overprikkeling',
    'Perfectionisme',
    'Perinataal',
    'Persoonlijkheidsproblematiek',
    'PST',
    'Psycho-oncologie',
    'Psychose',
    'Relatie',
    'Rouw',
    'Schematherapie',
    'Seksuologie',
    'Slaapproblemen',
    'Sociale angst',
    'SOLK',
    'Stress',
    'Suïcide',
    'Transculturele zorg',
    'Trauma',
    'VERS',
    'Verslaving',
    'Welzijn en ondersteuning',
    'Werk en studie',
    'Zelfbeeld',
    'Zingeving',
];
/** Set form for O(1) membership checks in the seed normalizer. */
export const CANONICAL_SPECIALISATIONS_SET = new Set(CANONICAL_SPECIALISATIONS);
/**
 * Weergavenaam per label, waar die afwijkt van de opgeslagen waarde. Besluit
 * 8 okt 2026 (Linda/Mandy): "Depressie" heet in de app "Stemming & depressie".
 * ponytail: alleen de weergave is hernoemd, niet de opgeslagen waarde. Die zit
 * in providers, documenten + hun chunks, gebruikersprofielen en de LVVP-crawler;
 * in data hernoemen is een migratie over vijf plekken voor een tekstwijziging.
 * Echte rename pas als de oude naam ergens zichtbaar blijft lekken.
 */
const SPECIALISATION_DISPLAY = {
    Depressie: 'Stemming & depressie',
};
export function specialisationLabel(value) {
    return SPECIALISATION_DISPLAY[value] ?? value;
}
/** Weergavenamen voor `CareLevel` (shared/src/schemas/provider.ts). */
export const CARE_LEVEL_LABELS = {
    basis_ggz: 'Basis-GGZ',
    specialistische_ggz: 'Specialistische GGZ',
    tertiair: 'Topklinisch/tertiair',
};
/**
 * Curated Nederlandse zorgverzekeraars, gegroepeerd per concern/label. Voedt
 * de verzekeraar-specifieke aanmeldstop op een praktijk ("neemt geen cliënten
 * meer aan die verzekerd zijn bij Zilveren Kruis/FBTO/..."). Gestructureerd
 * i.p.v. vrije tekst zodat de POH in één oogopslag ziet of háár patiënt nog
 * terecht kan, en later filterbaar. Labels binnen een concern delen vaak één
 * inkoopbeleid (het 1nP-geval: alle Achmea-labels dicht), daarom de groepering:
 * de UI kan een heel concern in één klik aanvinken.
 *
 * Curated enum, net als de specialisaties: een label toevoegen is bewust een
 * code-wijziging. Onbekende labels weigert de schrijfroute.
 */
export const INSURER_GROUPS = [
    {
        concern: 'Zilveren Kruis (Achmea)',
        labels: [
            'Zilveren Kruis',
            'FBTO',
            'Interpolis',
            'De Friesland',
            'ZieZo',
            'Pro Life',
            'De Christelijke Zorgverzekeraar',
        ],
    },
    {
        concern: 'VGZ',
        labels: ['VGZ', 'IZA', 'IZZ', 'Univé', 'Zekur', 'Bewuzt', 'UnitedConsumers'],
    },
    {
        concern: 'CZ',
        labels: ['CZ', 'CZdirect', 'Just', 'OHRA', 'Nationale-Nederlanden'],
    },
    { concern: 'Menzis', labels: ['Menzis', 'Anderzorg', 'HEMA'] },
    { concern: 'DSW', labels: ['DSW', 'Stad Holland', 'inTwente'] },
    { concern: 'ONVZ', labels: ['ONVZ', 'VvAA', 'Jaaah', 'PNOzorg'] },
    { concern: 'Zorg en Zekerheid', labels: ['Zorg en Zekerheid', 'AZVZ'] },
    { concern: 'a.s.r.', labels: ['a.s.r.', 'Ditzo'] },
    { concern: 'Eno', labels: ['Salland', 'HollandZorg', 'ZorgDirect'] },
    { concern: 'Aevitae', labels: ['Aevitae', 'Eucare'] },
];
/** Flat lijst van alle labels — voor validatie en losse rendering. */
export const CANONICAL_INSURERS = INSURER_GROUPS.flatMap((g) => g.labels);
/** Set form for O(1) membership checks op de schrijfroute. */
export const CANONICAL_INSURERS_SET = new Set(CANONICAL_INSURERS);
/**
 * Canonical document-type vocabulary for the kennisbank corpus. Used by
 * the Python ingest tagger (auto-classifies on title + first chunk),
 * the /documenten Type filter, and the /beheer inline editor. Distinct
 * from specialisations: this is the *genre* of the document
 * (werkboek vs richtlijn vs vragenlijst), not the clinical topic.
 *
 * Values are stored on `DocumentRegistryEntry.documentType` and on
 * every chunk (denormalized). Adding a value requires a code change so
 * the inline editor's dropdown stays in lockstep with what the auto-tagger
 * is allowed to emit. Keep alphabetical.
 */
export const CANONICAL_DOCUMENT_TYPES = [
    'Beleid',
    'Casus',
    'Folder',
    'Handleiding',
    'Handout',
    'LESA',
    'Onderzoek',
    'Protocol',
    'Richtlijn',
    'Standaard',
    'Vragenlijst',
    'Werkboek',
    'Zelfhulp',
];
export const CANONICAL_DOCUMENT_TYPES_SET = new Set(CANONICAL_DOCUMENT_TYPES);
/**
 * Full-scan ceiling for the Sociale Kaart search: the max providers the
 * backend reads (and re-sorts/filters) in one request, and the frontend's
 * "Alle" page size + map-view limit. One shared number so the two never drift
 * (they did once: the frontend hard-coded 1000 to match the backend).
 *
 * Set comfortably above the live active-set size (~3.1k nov 2026) so the whole
 * directory fits in a single read and the surname re-sort below is correct
 * (a title-biased Firestore window would otherwise truncate the tail).
 * De read gebeurt sinds #257 één keer per TTL-venster van 5 minuten in plaats
 * van per request (zie `ProviderService.allWithStatus`), dus de leeslast groeit
 * niet meer mee met het aantal zoekopdrachten. Gemeten 21 sep 2026 bij 2860
 * records: 2,36 MB en 83 ms Zod-parse per vulling.
 * ponytail: O(corpus) per cache-vulling. Nadert de actieve set dit plafond,
 * meet dan opnieuw wat één vulling kost voordat je het verhoogt; bij een
 * veelvoud is een zoek-backend (Algolia / Vertex AI Search) het antwoord.
 */
export const PROVIDER_SEARCH_CEILING = 6000;
// Titels (academisch + aanhef) die vóór een naam kunnen staan. Case-insensitive,
// punt optioneel. Alleen relevant als signaal dat het om een persoon gaat.
const NAME_TITLES = new Set([
    'dr',
    'drs',
    'prof',
    'mr',
    'ir',
    'ing',
    'bc',
    'msc',
    'bsc',
    'ba',
    'ma',
    'phd',
    'med',
    'mw',
    'mevr',
    'mevrouw',
    'dhr',
    'hr',
]);
// Nederlandse tussenvoegsels: genegeerd bij het bepalen van de sorteerletter
// (telefoonboek-conventie), zodat "M. van der Berg" onder B valt, niet onder V.
const TUSSENVOEGSELS = new Set([
    'van',
    'de',
    'der',
    'den',
    'het',
    "'t",
    'ten',
    'ter',
    'te',
    'op',
    'aan',
    'tot',
    'bij',
    'uit',
    'over',
    'onder',
    'in',
    'vd',
    'vande',
    'vander',
]);
function stripDiacritics(s) {
    return s.normalize('NFKD').replace(/[̀-ͯ]/g, '');
}
/** Is dit token een titel (drs, dr, prof, ...)? Punt optioneel, case-insensitive. */
function isTitleToken(tok) {
    return NAME_TITLES.has(tok.replace(/\.$/, '').toLowerCase());
}
/**
 * Ziet dit token eruit als initialen of een roepnaam-tussenzetsel?
 *   - gepunte initialen: "M.", "M.M.", "N.H.P.", "m.a.s."
 *   - losse letter:      "A"
 *   - ongepunte hoofdletter-cluster: "JTWM", "MAWVC" (LVVP schrijft zo)
 *   - roepnaam tussen haakjes: "(Nicole)"
 */
function isInitialToken(tok) {
    if (/^\(.+\)$/.test(tok))
        return true; // (Nicole)
    if (/^([A-Za-zÀ-ÿ]\.)+[A-Za-zÀ-ÿ]?\.?$/.test(tok))
        return true; // M.M. / N.H.P.
    if (/^[A-Za-zÀ-ÿ]$/.test(tok))
        return true; // losse letter
    if (/^[A-ZÀ-Ÿ]{2,6}$/.test(tok))
        return true; // JTWM
    return false;
}
/**
 * Sorteersleutel voor een aanbieder: sorteer op **achternaam**, niet op titel.
 *
 * De meeste LVVP-namen zijn "Drs. J.T.W.M. Roeffen" - één vrij tekstveld met een
 * titel + initialen ervoor, waardoor ze allemaal onder D/d belanden. Deze functie
 * pelt de titel + initialen (+ roepnaam) af en negeert Nederlandse tussenvoegsels,
 * zodat "Drs. M. van der Berg" onder B sorteert.
 *
 * Alleen namen mét een leidende titel worden als persoon behandeld en afgepeld -
 * dat is het enige betrouwbare persoon-signaal. Organisatienamen ("GGZ inGeest",
 * "Altrecht", "113 Zelfmoordpreventie") missen een titel en sorteren op de hele
 * naam, zodat we ze niet per ongeluk kapot-strippen.
 *
 * ponytail: heuristiek, geen NLP. Titelloze persoonsnamen ("Anja Bos") sorteren
 * op voornaam - niet perfect, maar dat was niet de klacht en gokken mis-sorteert
 * organisaties. Uitbreiden als Linda titelloze voornaam-sortering meldt.
 */
export function providerSortKey(name) {
    const raw = name.trim().replace(/\s+/g, ' ');
    if (!raw)
        return '';
    const tokens = raw.split(' ');
    // 1. Leidende titels afpellen (kan er meer dan één zijn: "Prof. dr.").
    let i = 0;
    let hadTitle = false;
    while (i < tokens.length - 1 && isTitleToken(tokens[i])) {
        hadTitle = true;
        i++;
    }
    // Geen titel -> organisatie/titelloos: sorteer op de hele naam.
    if (!hadTitle)
        return stripDiacritics(raw.toLowerCase());
    // 2. Initialen + roepnaam afpellen (nooit het laatste token opeten).
    while (i < tokens.length - 1 && isInitialToken(tokens[i]))
        i++;
    // 3. Tussenvoegsels vooraan negeren (nooit het laatste token opeten).
    while (i < tokens.length - 1 && TUSSENVOEGSELS.has(tokens[i].toLowerCase()))
        i++;
    const rest = tokens.slice(i).join(' ').trim();
    const key = rest || raw; // alles weg-gestript -> val terug op de hele naam
    return stripDiacritics(key.toLowerCase());
}
/**
 * Letterbalk-bucket voor een aanbieder: de eerste letter van de sorteersleutel
 * (dus de achternaam), uppercased. '#' voor namen die niet met A-Z beginnen
 * (cijfers zoals "113", of symbolen).
 */
export function providerLetterBucket(name) {
    const first = (providerSortKey(name).charAt(0) || '').toUpperCase();
    return first >= 'A' && first <= 'Z' ? first : '#';
}
//# sourceMappingURL=sociale-kaart.js.map